import { useLayoutEffect, useState, type RefObject } from 'react';

import { isLoadingSummary } from './AsideModule';
import { stripFills } from './strip-fills';

// Set on the strip while its cards are laid out at their content widths
// (no wrapping) to measure them.
const MEASURE_ATTRIBUTE = 'data-fill-measure';

/**
 * Whether the strip's cards fill the row (see strip-fills). Measured
 * synchronously before the first paint, then again — at most once a frame —
 * whenever the row resizes, a card's content changes (a module finishing
 * loading) or a web font loads. Each measurement lays the row out twice
 * (unwrapped, then back), so bursts of changes share one.
 */
export function useStripFills(rowRef: RefObject<HTMLElement | null>, active: boolean): boolean {
  const [fills, setFills] = useState(false);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!active || row === null) return;
    const measure = () => {
      row.setAttribute(MEASURE_ATTRIBUTE, '');
      const contentWidths = [...row.children].map((card) =>
        isLoadingSummary(card) ? Number.POSITIVE_INFINITY : card.getBoundingClientRect().width,
      );
      const style = getComputedStyle(row);
      row.removeAttribute(MEASURE_ATTRIBUTE);
      setFills(
        stripFills({
          contentWidths,
          rowWidth:
            row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight),
          gap: parseFloat(style.columnGap),
          // The scrolling layout's count of whole cards, set by the strip's
          // container query.
          wholeCards: parseInt(style.getPropertyValue('--strip-count'), 10),
        }),
      );
    };
    let frame = 0;
    const scheduleMeasure = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    measure();
    const resizes = new ResizeObserver(scheduleMeasure);
    resizes.observe(row);
    const edits = new MutationObserver(scheduleMeasure);
    edits.observe(row, { childList: true, subtree: true, characterData: true });
    document.fonts.addEventListener('loadingdone', scheduleMeasure);
    return () => {
      cancelAnimationFrame(frame);
      resizes.disconnect();
      edits.disconnect();
      document.fonts.removeEventListener('loadingdone', scheduleMeasure);
    };
  }, [rowRef, active]);
  return fills;
}
